/**
 * HotelEnrichment Component
 * Displays scraped hotel content with option to trigger scraping from hotel websites
 */

import React, { useState, useEffect } from 'react';

interface ScrapedImage {
    url: string;
    alt: string;
}

interface RoomType {
    name: string;
    description?: string;
    price?: string;
}

interface ScrapedContent {
    _id: string;
    hotelName: string;
    sourceUrl: string;
    title?: string;
    description?: string;
    englishDescription?: string;
    roomTypes: RoomType[];
    amenities: string[];
    images: ScrapedImage[];
    contact?: {
        phone?: string;
        email?: string;
        address?: string;
    };
    scrapeStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
    translationStatus: 'pending' | 'in_progress' | 'completed' | 'failed' | 'not_needed';
    scrapedAt?: string;
}

interface HotelEnrichmentProps {
    hotelCode?: string;
    hotelName: string;
    hotelWebsite?: string;
    onContentLoaded?: (content: ScrapedContent) => void;
    showButton?: boolean;
    autoLoad?: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

const HotelEnrichment: React.FC<HotelEnrichmentProps> = ({
    hotelCode,
    hotelName,
    hotelWebsite,
    onContentLoaded,
    showButton = true,
    autoLoad = false
}) => {
    const [content, setContent] = useState<ScrapedContent | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [scraping, setScraping] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [showGallery, setShowGallery] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    // Load existing content on mount if autoLoad is enabled
    useEffect(() => {
        if (autoLoad && hotelCode) {
            loadExistingContent();
        }
    }, [hotelCode, autoLoad]);

    const loadExistingContent = async () => {
        if (!hotelCode) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/api/hotel-scraper/by-code/${hotelCode}`);
            const data = await response.json();

            if (data.success && data.data) {
                setContent(data.data);
                onContentLoaded?.(data.data);
            }
        } catch (err) {
            // Silent fail for auto-load - no content yet is fine
            console.log('No scraped content found for hotel:', hotelCode);
        } finally {
            setLoading(false);
        }
    };

    const triggerScrape = async (forceRescrape = false) => {
        if (!hotelWebsite) {
            setError('No hotel website URL available');
            return;
        }

        setScraping(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/api/hotel-scraper/scrape`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    url: hotelWebsite,
                    hotelName: hotelName,
                    tourvisioHotelCode: hotelCode,
                    forceRescrape
                })
            });

            const data = await response.json();

            if (data.success) {
                setContent(data.data);
                onContentLoaded?.(data.data);
            } else {
                setError(data.error || 'Scraping failed');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to scrape website');
        } finally {
            setScraping(false);
        }
    };

    const triggerTranslation = async () => {
        if (!content?._id) return;

        setTranslating(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/api/hotel-scraper/${content._id}/translate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (data.success) {
                setContent(data.data);
                onContentLoaded?.(data.data);
            } else {
                setError(data.error || 'Translation failed');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to translate content');
        } finally {
            setTranslating(false);
        }
    };

    const StatusBadge = ({ status, type }: { status: string; type: 'scrape' | 'translate' }) => {
        const colors: Record<string, string> = {
            pending: 'bg-yellow-500/20 text-yellow-400',
            in_progress: 'bg-blue-500/20 text-blue-400',
            completed: 'bg-green-500/20 text-green-400',
            failed: 'bg-red-500/20 text-red-400',
            not_needed: 'bg-gray-500/20 text-gray-400'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
                {type === 'scrape' ? 'Scrape' : 'Translation'}: {status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                <span className="ml-3 text-gray-400">Loading enriched content...</span>
            </div>
        );
    }

    return (
        <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="material-icons text-purple-400">auto_awesome</span>
                    <h3 className="text-lg font-semibold text-white">Enriched Hotel Content</h3>
                </div>

                <div className="flex items-center gap-2">
                    {content && (
                        <>
                            <StatusBadge status={content.scrapeStatus} type="scrape" />
                            <StatusBadge status={content.translationStatus} type="translate" />
                        </>
                    )}

                    {showButton && hotelWebsite && (
                        <button
                            onClick={() => triggerScrape(!!content)}
                            disabled={scraping}
                            className="ml-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 
                                       text-white text-sm font-medium rounded-lg flex items-center gap-2 transition-colors"
                        >
                            {scraping ? (
                                <>
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                    Scraping...
                                </>
                            ) : (
                                <>
                                    <span className="material-icons text-sm">download</span>
                                    {content ? 'Rescrape' : 'Fetch from Website'}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="px-6 py-3 bg-red-500/10 border-b border-red-500/30">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                        <span className="material-icons text-sm">error</span>
                        {error}
                    </p>
                </div>
            )}

            {!content ? (
                <div className="p-8 text-center">
                    <span className="material-icons text-6xl text-gray-600 mb-4">language</span>
                    <p className="text-gray-400 mb-2">No enriched content available</p>
                    {hotelWebsite ? (
                        <p className="text-gray-500 text-sm">
                            Click "Fetch from Website" to scrape content from the hotel's website
                        </p>
                    ) : (
                        <p className="text-gray-500 text-sm">
                            Hotel website URL is required to fetch enriched content
                        </p>
                    )}
                </div>
            ) : (
                <div className="p-6 space-y-6">
                    {/* Source Info */}
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                        <span className="material-icons text-sm">link</span>
                        <a href={content.sourceUrl} target="_blank" rel="noopener noreferrer"
                            className="hover:text-blue-400 transition-colors">
                            {content.sourceUrl}
                        </a>
                        {content.scrapedAt && (
                            <span className="ml-4">
                                Scraped: {new Date(content.scrapedAt).toLocaleString()}
                            </span>
                        )}
                    </div>

                    {/* Description */}
                    {(content.description || content.englishDescription) && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h4 className="text-white font-medium flex items-center gap-2">
                                    <span className="material-icons text-sm">description</span>
                                    Description
                                </h4>
                                {content.description && !content.englishDescription &&
                                    content.translationStatus !== 'completed' && (
                                        <button
                                            onClick={triggerTranslation}
                                            disabled={translating}
                                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600
                                                   text-white text-xs font-medium rounded-lg flex items-center gap-1"
                                        >
                                            {translating ? (
                                                <>
                                                    <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                                                    Translating...
                                                </>
                                            ) : (
                                                <>
                                                    <span className="material-icons text-xs">translate</span>
                                                    Translate to English
                                                </>
                                            )}
                                        </button>
                                    )}
                            </div>
                            <div className="bg-gray-900/50 rounded-lg p-4">
                                <p className="text-gray-300 whitespace-pre-line leading-relaxed">
                                    {content.englishDescription || content.description}
                                </p>
                                {content.englishDescription && content.description && (
                                    <details className="mt-4">
                                        <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400">
                                            View Original
                                        </summary>
                                        <p className="mt-2 text-gray-400 text-sm whitespace-pre-line">
                                            {content.description}
                                        </p>
                                    </details>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Room Types */}
                    {content.roomTypes.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <span className="material-icons text-sm">king_bed</span>
                                Room Types ({content.roomTypes.length})
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {content.roomTypes.map((room, index) => (
                                    <div key={index} className="bg-gray-900/50 rounded-lg p-4">
                                        <h5 className="text-white font-medium">{room.name}</h5>
                                        {room.description && (
                                            <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                                                {room.description}
                                            </p>
                                        )}
                                        {room.price && (
                                            <p className="text-green-400 text-sm mt-2 font-medium">
                                                {room.price}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Amenities */}
                    {content.amenities.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <span className="material-icons text-sm">spa</span>
                                Amenities ({content.amenities.length})
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {content.amenities.slice(0, 20).map((amenity, index) => (
                                    <span key={index}
                                        className="px-3 py-1 bg-gray-900/50 rounded-full text-sm text-gray-300">
                                        {amenity}
                                    </span>
                                ))}
                                {content.amenities.length > 20 && (
                                    <span className="px-3 py-1 bg-gray-700/50 rounded-full text-sm text-gray-400">
                                        +{content.amenities.length - 20} more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Images Gallery */}
                    {content.images.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <span className="material-icons text-sm">photo_library</span>
                                Images ({content.images.length})
                            </h4>
                            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                {content.images.slice(0, 16).map((image, index) => (
                                    <div
                                        key={index}
                                        onClick={() => { setActiveImageIndex(index); setShowGallery(true); }}
                                        className="aspect-square rounded-lg overflow-hidden cursor-pointer 
                                                   hover:ring-2 hover:ring-purple-500 transition-all"
                                    >
                                        <img
                                            src={image.url}
                                            alt={image.alt || `Hotel image ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                        />
                                    </div>
                                ))}
                                {content.images.length > 16 && (
                                    <div
                                        onClick={() => setShowGallery(true)}
                                        className="aspect-square rounded-lg bg-gray-700/50 flex items-center 
                                                   justify-center cursor-pointer hover:bg-gray-700 transition-colors"
                                    >
                                        <span className="text-gray-300 text-sm font-medium">
                                            +{content.images.length - 16}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Contact Info */}
                    {content.contact && (content.contact.phone || content.contact.email || content.contact.address) && (
                        <div className="space-y-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <span className="material-icons text-sm">contacts</span>
                                Contact Information
                            </h4>
                            <div className="bg-gray-900/50 rounded-lg p-4 space-y-2">
                                {content.contact.phone && (
                                    <p className="text-gray-300 flex items-center gap-2">
                                        <span className="material-icons text-sm text-gray-500">phone</span>
                                        {content.contact.phone}
                                    </p>
                                )}
                                {content.contact.email && (
                                    <p className="text-gray-300 flex items-center gap-2">
                                        <span className="material-icons text-sm text-gray-500">email</span>
                                        {content.contact.email}
                                    </p>
                                )}
                                {content.contact.address && (
                                    <p className="text-gray-300 flex items-center gap-2">
                                        <span className="material-icons text-sm text-gray-500">location_on</span>
                                        {content.contact.address}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Fullscreen Gallery Modal */}
            {showGallery && content && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setShowGallery(false)}>
                    <button
                        onClick={() => setShowGallery(false)}
                        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                    >
                        <span className="material-icons text-3xl">close</span>
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); setActiveImageIndex(i => Math.max(0, i - 1)); }}
                        className="absolute left-4 text-white hover:text-gray-300 z-10"
                    >
                        <span className="material-icons text-4xl">chevron_left</span>
                    </button>

                    <img
                        src={content.images[activeImageIndex]?.url}
                        alt={content.images[activeImageIndex]?.alt || 'Hotel image'}
                        className="max-w-full max-h-[90vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />

                    <button
                        onClick={(e) => { e.stopPropagation(); setActiveImageIndex(i => Math.min(content.images.length - 1, i + 1)); }}
                        className="absolute right-4 text-white hover:text-gray-300 z-10"
                    >
                        <span className="material-icons text-4xl">chevron_right</span>
                    </button>

                    <div className="absolute bottom-4 text-white text-sm">
                        {activeImageIndex + 1} / {content.images.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HotelEnrichment;
